// src/screens/AllTrainers.tsx
import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Search } from "lucide-react";
import Title from "../components/Title";
import CreditsBalance from "../components/CreditBalance";
import Navbar from "../components/Navbar";
import TrainerScheduleCard from "../components/TrainerScheduleCard";
import { getAllTrainers, checkSlotAvailability, getUserProfile, getTrainerProfileWithAvailability } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { formatTimeSlot, utcTimeOfDayToLocal } from "../utils/timeUtils";
import trainerImg from "../assets/trainer.png";
import { UPLOADS_BASE_URL } from "../config/api";

/* --------------------------------- types ---------------------------------- */
type AllTrainer = {
  _id: string;
  name: string;
  rate: number;
  age: number;
  sex: "M" | "F";
  schedule: { day: string; time: string }[];
  imageUrl?: string;
  creditsPerHour?: number;
  availability?: any[];
  fullTrainerData?: any;
};

/* ------------------------------ components -------------------------------- */

const SearchBar: React.FC<{ value: string; onChange: (value: string) => void }> = ({
  value,
  onChange,
}) => {
  return (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        placeholder="Search Trainer"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-12 pl-10 pr-4 border border-gray-300 rounded bg-white text-[14px] placeholder:text-gray-400 focus:outline-none focus:border-[#D62422]"
      />
    </div>
  );
};

const TrainerCard: React.FC<{ trainer: AllTrainer; onClick: () => void }> = ({
  trainer,
  onClick,
}) => {
  return (
    <div className="mb-4">
      <TrainerScheduleCard
        name={trainer.name}
        rate={trainer.creditsPerHour || trainer.rate}
        gradient="dark"
        meta={`${trainer.age}${trainer.sex}`}
        imageSrc={String(trainer.imageUrl || trainerImg)}
        imgHeight={140}
        bandHeight={100}
        schedule={trainer.schedule}
        onClick={onClick}
      />
    </div>
  );
};

/* --------------------------------- screen --------------------------------- */
export default function AllTrainers() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState("");
  const [trainers, setTrainers] = useState<AllTrainer[]>([]);
  const [userCredits, setUserCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [creditsLoading, setCreditsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user credits
  const fetchUserCredits = async () => {
    if (!user?.id) return;

    try {
      setCreditsLoading(true);
      const profileResponse = await getUserProfile(user.id);
      console.log('AllTrainers - Profile response:', profileResponse);
      setUserCredits(profileResponse.user?.credits || 0);
    } catch (error) {
      console.error('AllTrainers - Error fetching user credits:', error);
    } finally {
      setCreditsLoading(false);
    }
  };

  // Fetch trainers and user data
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        console.log('AllTrainers - Starting to fetch data for user:', user.id);

        // Fetch trainers and user profile in parallel
        const [trainersResponse, profileResponse] = await Promise.all([
          getAllTrainers(),
          getUserProfile(user.id)
        ]);

        console.log('AllTrainers - Trainers response:', trainersResponse);
        console.log('AllTrainers - Profile response:', profileResponse);

        const trainersData = trainersResponse.trainers || [];
        setUserCredits(profileResponse.user?.credits || 0);

        // Format trainers with availability data
        const formattedTrainers = trainersData.map((trainer: any) => {
          try {
            // Get the next 3 days
            const today = new Date();
            const next3Days = [];

            for (let i = 0; i < 3; i++) {
              const date = new Date(today);
              date.setDate(today.getDate() + i);
              const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
              
              // Get the YYYY-MM-DD local format string
              const year = date.getFullYear();
              const month = (date.getMonth() + 1).toString().padStart(2, '0');
              const day = date.getDate().toString().padStart(2, '0');
              const dateString = `${year}-${month}-${day}`;

              next3Days.push({
                dayName,
                dateString,
                isToday: i === 0
              });
            }

            // Get availability for each day using pre-calculated slots
            const schedule = next3Days.map((day, index) => {
              const daySlots = trainer.availabilitySlots?.[day.dateString];

              if (daySlots && Array.isArray(daySlots) && daySlots.length > 0) {
                // Find first available slot
                const firstAvailableSlot = daySlots.find(slot => slot.available);

                if (firstAvailableSlot) {
                  const zIndex = firstAvailableSlot.slot.indexOf('Z');
                  let startTimeLocal = "";
                  let endTimeLocal = "";
                  
                  if (zIndex !== -1) {
                      const startISO = firstAvailableSlot.slot.substring(0, zIndex + 1);
                      const endISO = firstAvailableSlot.slot.substring(zIndex + 2);
                      
                      const startDate = new Date(startISO);
                      const endDate = new Date(endISO);
                      
                      startTimeLocal = startDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                      
                      endTimeLocal = endDate.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                  }

                  return {
                    day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                    time: startTimeLocal && endTimeLocal ? `${startTimeLocal} - ${endTimeLocal}` : 'Busy'
                  };
                } else {
                  return {
                    day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                    time: 'Busy'
                  };
                }
              }

              return {
                day: day.isToday ? 'Today' : index === 1 ? 'Tomorrow' : day.dayName,
                time: 'Busy'
              };
            });

            // Use signedProfilePic or profilePic from backend
            let imageUrl = trainerImg;
            const picUrl = trainer.signedProfilePic || trainer.profilePic;
            
            if (picUrl) {
              if (picUrl.startsWith('http://') || picUrl.startsWith('https://')) {
                imageUrl = picUrl;
              } else if (picUrl.startsWith('trainer/')) {
                imageUrl = `${UPLOADS_BASE_URL}/${picUrl}`;
              } else {
                imageUrl = picUrl;
              }
            }

            return {
              _id: trainer._id,
              name: trainer.name,
              rate: trainer.creditsPerHour || 0,
              age: trainer.age ? parseInt(trainer.age) : 0,
              sex: trainer.sex === 'Male' ? 'M' : 'F',
              schedule: schedule,
              imageUrl: imageUrl,
              creditsPerHour: trainer.creditsPerHour,
              availability: trainer.availability,
              fullTrainerData: trainer // Pass the full trainer object as it has everything we need
            };
          } catch (error) {
            console.error(`Error formatting trainer ${trainer.name}:`, error);
            
            let imageUrl = trainerImg;
            if (trainer.profilePic) {
              if (trainer.profilePic.startsWith('http://') || trainer.profilePic.startsWith('https://')) {
                imageUrl = trainer.profilePic;
              } else if (trainer.profilePic.startsWith('trainer/')) {
                imageUrl = `${UPLOADS_BASE_URL}/${trainer.profilePic}`;
              } else {
                imageUrl = trainer.profilePic;
              }
            }
            return {
              _id: trainer._id,
              name: trainer.name,
              rate: trainer.creditsPerHour || 0,
              age: trainer.age ? parseInt(trainer.age) : 0,
              sex: trainer.sex === 'Male' ? 'M' : 'F',
              schedule: [
                { day: 'Today', time: 'Busy' },
                { day: 'Tomorrow', time: 'Busy' },
                { day: 'Day after', time: 'Busy' }
              ],
              imageUrl: imageUrl,
              creditsPerHour: trainer.creditsPerHour,
              availability: trainer.availability
            };
          }
        });

        console.log('AllTrainers - Formatted trainers:', formattedTrainers);
        setTrainers(formattedTrainers);
      } catch (error) {
        console.error('AllTrainers - Error loading data:', error);
        setError('Failed to load trainers');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user?.id]);

  // Refresh credits when screen comes into focus
  useEffect(() => {
    const handleFocus = () => {
      fetchUserCredits();
    };

    // Add event listener for when the window regains focus
    window.addEventListener('focus', handleFocus);

    // Also refresh credits when component mounts
    fetchUserCredits();

    return () => {
      window.removeEventListener('focus', handleFocus);
    };
  }, [user?.id]);

  // Refresh credits when navigating back to this screen
  useEffect(() => {
    fetchUserCredits();
  }, [location.pathname, user?.id]);

  const handleTrainerClick = (trainer: AllTrainer) => {
    navigate("/student/trainer-details", { state: { trainer } });
  };

  // Filter trainers based on search query
  const filteredTrainers = trainers.filter(trainer =>
    (trainer.name || "").toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="h-[100dvh] bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full pt-[max(12px,env(safe-area-inset-top))]">
        <Navbar
          onBack={() => navigate("/student/explore-trainers")}
          background="transparent"
          right={
            <CreditsBalance
              balance={creditsLoading ? 0 : userCredits}
              variant="white-with-button"
              onAdd={() => navigate("/student/buy-credits")}
            />
          }
        />

        {/* Content */}
        <main className="px-5">
          {/* Section Title */}
          <Title text="// ALL TRAINERS" />

          {/* Search Bar */}
          <div className="mt-4 mb-6">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
            />
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="text-[#8A8A8A]">Loading trainers...</div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="flex justify-center items-center py-8">
              <div className="text-[#EB2726]">{error}</div>
            </div>
          )}

          {/* Trainer List */}
          {!loading && !error && (
            <div className="pb-20">
              {filteredTrainers.length === 0 ? (
                <div className="flex justify-center items-center py-8">
                  <div className="text-[#8A8A8A]">
                    {searchQuery ? 'No trainers found matching your search.' : 'No trainers available.'}
                  </div>
                </div>
              ) : (
                filteredTrainers.map((trainer, index) => (
                  <TrainerCard
                    key={`${trainer._id}-${index}`}
                    trainer={trainer}
                    onClick={() => handleTrainerClick(trainer)}
                  />
                ))
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
